import { signal, effect } from "@preact/signals";
import type { HighlightColor } from "./types";

export type Theme = "light" | "dark" | "sepia";
export type ReaderFont = "serif" | "sans";

const KEY = "stash.settings.v1";

interface Settings {
  theme: Theme;
  readerFont: ReaderFont;
  readerFontSize: number; // px
  readerLineHeight: number;
  readerColumn: number; // rem
  defaultHighlightColor: HighlightColor;
}

const defaults: Settings = {
  theme: "light",
  readerFont: "serif",
  readerFontSize: 18,
  readerLineHeight: 1.6,
  readerColumn: 38,
  defaultHighlightColor: "yellow",
};

function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return defaults;
  }
}

export const settings = signal<Settings>(load());

effect(() => {
  const s = settings.value;
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* ignore quota errors */
  }
  const root = document.documentElement;
  root.setAttribute("data-theme", s.theme);
  root.style.setProperty("--reader-font", s.readerFont === "serif" ? "var(--font-serif)" : "var(--font-sans)");
  root.style.setProperty("--reader-font-size", `${s.readerFontSize}px`);
  root.style.setProperty("--reader-line-height", String(s.readerLineHeight));
  root.style.setProperty("--reader-column", `${s.readerColumn}rem`);
  // theme-color meta for mobile chrome
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", s.theme === "dark" ? "#1a1a1a" : s.theme === "sepia" ? "#f5edd6" : "#faf9f5");
  }
});

export function update(patch: Partial<Settings>) {
  settings.value = { ...settings.value, ...patch };
}
